import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { ShieldAlert, Eye, Loader2, ChevronRight } from 'lucide-react';

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const roleColors = {
  admin: 'bg-violet-100 text-violet-700',
  coordinator: 'bg-sky-100 text-sky-700',
  facilitator: 'bg-amber-100 text-amber-700',
  peer_specialist: 'bg-rose-100 text-rose-700',
  referral_source: 'bg-indigo-100 text-indigo-700',
  court_officer: 'bg-slate-100 text-slate-700',
};

export default function RecordAuditPanel({ participantId }) {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);

  // Only admins + coordinators can see this; render nothing otherwise
  const canView = user && ['admin', 'coordinator', 'network_admin'].includes(user.role);

  useEffect(() => {
    if (!canView || !participantId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/audit/record-views/${participantId}?days=90`);
        setData(res.data);
      } catch {
        setData(null);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId, canView]);

  if (!canView) return null;

  return (
    <>
      <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-indigo-400" data-testid="record-audit-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ShieldAlert size={16} className="text-indigo-600" /> Record Access Audit
          </CardTitle>
          <p className="text-xs text-muted-foreground">HIPAA-adjacent compliance: who has accessed this participant's record in the last 90 days</p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="animate-spin text-indigo-600" size={20} /></div>
          ) : data && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-indigo-50 rounded-xl p-3">
                  <p className="text-2xl font-bold text-indigo-700">{data.total_views}</p>
                  <p className="text-xs text-indigo-600">Record Views</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3">
                  <p className="text-2xl font-bold text-amber-700">{data.unique_viewers}</p>
                  <p className="text-xs text-amber-600">Unique Viewers</p>
                </div>
              </div>

              {data.viewers.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-3">No one has accessed this record yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.viewers.slice(0, 5).map(v => (
                    <div key={v.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold shrink-0">
                        {v.user_email?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{v.user_email}</p>
                        <p className="text-[10px] text-slate-500">
                          {v.view_count} view{v.view_count !== 1 ? 's' : ''} · {v.unique_endpoints} endpoints · last {timeAgo(v.last_viewed)}
                        </p>
                      </div>
                      <Badge className={`${roleColors[v.user_role] || 'bg-slate-100 text-slate-600'} border-0 text-[9px]`}>{v.user_role}</Badge>
                    </div>
                  ))}
                  {data.recent_views.length > 0 && (
                    <button
                      onClick={() => setDetailOpen(true)}
                      className="w-full text-xs text-indigo-600 hover:text-indigo-800 mt-2 py-1 flex items-center justify-center gap-1"
                      data-testid="record-audit-view-all"
                    >
                      View full access log ({data.recent_views.length} entries) <ChevronRight size={12} />
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Full log dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye size={18} /> Record Access Log</DialogTitle>
            <DialogDescription>Every recorded access to this participant's record (last 90 days)</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-1 pr-3">
              {data?.recent_views?.map((r, idx) => (
                <div key={`${r.timestamp}-${idx}`} className="flex items-center gap-2 p-2 text-xs border-b border-slate-100">
                  <Badge className={`${roleColors[r.viewer_role] || 'bg-slate-100 text-slate-600'} border-0 text-[9px] shrink-0`}>{r.viewer_role}</Badge>
                  <span className="font-medium truncate min-w-0">{r.viewer_email}</span>
                  <code className="font-mono text-slate-500 flex-1 truncate text-[10px]">{r.path}</code>
                  <span className="text-slate-400 shrink-0 text-[10px]">{r.ip}</span>
                  <span className="text-slate-400 shrink-0 text-[10px]">{timeAgo(r.timestamp)}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
